import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { styled } from '@mui/material/styles';
import { 
  Typography, Box, Paper, IconButton, Menu, MenuItem, Dialog, 
  DialogTitle, DialogContent, DialogActions, Button, TextField, 
  List, ListItem, ListItemText, Divider, DialogContentText,
  Tooltip, InputAdornment
} from '@mui/material';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import MenuIcon from '@mui/icons-material/Menu';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';

function SortableSwimmer({ 
  swimmer, 
  swimmersData, 
  currentSwimmer, 
  formatTime, 
  isRunning, 
  isPaused,
  onDelete, 
  onDoubleClick, 
  onEdit,
  swimmersList = [],
  poolLength,
  raceDistance,
  disableEdit
}) {
  const [anchorEl, setAnchorEl] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editedName, setEditedName] = useState(swimmer);
  const [editedLaps, setEditedLaps] = useState([]);
  const [deleteLapDialogOpen, setDeleteLapDialogOpen] = useState(false);
  const [lapToDelete, setLapToDelete] = useState(null);
  const [nameError, setNameError] = useState('');
  const [editingLapIndex, setEditingLapIndex] = useState(null);
  const [editingLapTime, setEditingLapTime] = useState('');

  const getLastValidLapTime = () => {
    const swimmerData = swimmersData[swimmer];
    if (!swimmerData) return 0;
    
    const laps = swimmerData.laps || [];
    if (laps.length === 0) return 0;
    
    const lastLap = laps[laps.length - 1];
    return typeof lastLap === 'object' ? lastLap.time : lastLap;
  };
  
  useEffect(() => {
    setEditedName(swimmer);
    const currentLaps = swimmersData[swimmer]?.laps || [];
    const distance = raceDistance || poolLength;
    const formattedLaps = currentLaps.map(lap => ({
      time: typeof lap === 'object' ? lap.time : lap,
      distance: distance,
      edited: false,
      timestamp: new Date().toISOString()
    }));
    setEditedLaps(formattedLaps);
  }, [swimmer, swimmersData, editDialogOpen, poolLength, raceDistance]);

  useEffect(() => {
    if (editedName.trim() && editedName !== swimmer && Array.isArray(swimmersList)) {
      const nameExists = swimmersList.some(s => s.toLowerCase() === editedName.trim().toLowerCase());
      setNameError(nameExists ? 'Este nome já está em uso' : '');
    } else {
      setNameError('');
    }
  }, [editedName, swimmer, swimmersList]);
  
  const open = Boolean(anchorEl);
  const isCurrentSwimmer = swimmer === currentSwimmer;
  const isEditDisabled = isRunning && !isPaused;
  const isDeleteDisabled = isRunning && isCurrentSwimmer;

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({ id: swimmer });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition
  };

  const totalDistance = (swimmersData[swimmer]?.laps?.length || 0) * (raceDistance || poolLength);
  const totalLaps = Math.ceil(totalDistance / poolLength);

  const handleMenuClick = (event) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleDelete = () => {
    if (!isDeleteDisabled) {
      handleClose();
      onDelete(swimmer);
    }
  };

  const handleEdit = () => {
    if (!isEditDisabled) {
      handleClose();
      setEditDialogOpen(true);
    }
  };

  const handleDeleteLapClick = (index) => {
    setLapToDelete(index);
    setDeleteLapDialogOpen(true);
  };

  const handleConfirmLapDelete = () => {
    if (lapToDelete !== null) {
      const newLaps = editedLaps.filter((_, i) => i !== lapToDelete);
      setEditedLaps(newLaps);
    }
    setDeleteLapDialogOpen(false);
    setLapToDelete(null);
  };

  const handleStartEditLap = (index, lap) => {
    setEditingLapIndex(index);
    const timeInSeconds = (lap.time && !isNaN(lap.time)) ? (lap.time / 1000).toFixed(2) : '0.00';
    setEditingLapTime(timeInSeconds);
  };

  const handleSaveLapEdit = () => {
    const newTime = parseFloat(editingLapTime) * 1000;
    const distance = raceDistance || poolLength;
    
    if (!isNaN(newTime) && newTime > 0) {
      const newLaps = [...editedLaps];
      newLaps[editingLapIndex] = {
        time: newTime,
        distance: distance,
        edited: true,
        timestamp: new Date().toISOString()
      };
      setEditedLaps(newLaps);
    }
    
    setEditingLapIndex(null);
    setEditingLapTime('');
  };

  const handleCancelLapEdit = () => {
    setEditingLapIndex(null);
    setEditingLapTime('');
    const distance = raceDistance || poolLength;
    setEditedLaps(swimmersData[swimmer]?.laps?.map(lap => ({
      time: typeof lap === 'object' ? lap.time : lap,
      distance: distance,
      edited: false,
      timestamp: new Date().toISOString()
    })) || []);
  };

  const handleSaveEdit = () => {
    if (editedName.trim() && !nameError) {
      try {
        const distance = raceDistance || poolLength;
        const processedLaps = editedLaps.map(lap => ({
          time: typeof lap === 'object' ? lap.time : parseFloat(lap),
          distance: distance,
          edited: true,
          timestamp: new Date().toISOString()
        }));

        const editData = {
          oldName: swimmer,
          newName: editedName.trim(),
          laps: processedLaps,
          distancePerLap: distance,
          poolLength: poolLength,
          raceDistance: raceDistance,
          lastUpdate: new Date().toISOString()
        };

        onEdit(editData);
        setEditDialogOpen(false);
        setEditingLapIndex(null);
        setEditingLapTime('');
      } catch (error) {
        console.error('Erro ao processar edição:', error);
      }
    }
  };

  const handleCloseDialog = () => {
    setEditDialogOpen(false);
    setEditedName(swimmer);
    const distance = raceDistance || poolLength;
    setEditedLaps(swimmersData[swimmer]?.laps?.map(lap => ({
      time: typeof lap === 'object' ? lap.time : lap,
      distance: distance,
      edited: false,
      timestamp: new Date().toISOString()
    })) || []);
    setEditingLapIndex(null);
    setEditingLapTime('');
  };

  return (
    <>
      <Paper
        ref={setNodeRef}
        style={style}
        {...attributes}
        {...listeners}
        elevation={1}
        sx={{
          p: { xs: 1.5, sm: 2, md: 3 },
          mb: 2,
          border: isCurrentSwimmer ? 2 : 0,
          borderColor: 'primary.main',
          bgcolor: isCurrentSwimmer ? 'primary.light' : 'background.paper',
          cursor: isRunning && isCurrentSwimmer ? 'default' : 'grab',
          userSelect: 'none',
          touchAction: 'none',
          opacity: isRunning && isCurrentSwimmer ? 0.8 : 1,
          pointerEvents: isRunning && isCurrentSwimmer ? 'none' : 'auto'
        }}
        onDoubleClick={() => !isEditDisabled && onDoubleClick(swimmer)}
      >
        <Box sx={{ 
          display: 'flex', 
          flexDirection: { xs: 'column', sm: 'row' },
          gap: { xs: 2, sm: 0 },
          justifyContent: 'space-between', 
          alignItems: { xs: 'flex-start', sm: 'center' }
        }}>
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 1,
            width: { xs: '100%', sm: 'auto' }
          }}>
            <Typography 
              variant="h6" 
              color={isCurrentSwimmer ? 'white' : 'primary'}
              sx={{ 
                fontSize: { xs: '1.1rem', sm: '1.25rem' },
                flexGrow: { xs: 1, sm: 0 }
              }}
            >
              {swimmer}
            </Typography>
            <IconButton 
              onClick={handleMenuClick}
              size="small"
              sx={{ 
                color: isCurrentSwimmer ? 'white' : 'inherit',
                opacity: isEditDisabled ? 0.5 : 1,
                '&:hover': {
                  backgroundColor: isCurrentSwimmer ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.04)'
                }
              }}
            >
              <MenuIcon />
            </IconButton>
          </Box>

          <Box sx={{ 
            display: 'grid',
            gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(4, auto)' },
            gap: { xs: 1.5, sm: 2 },
            width: { xs: '100%', sm: 'auto' },
            alignItems: 'center'
          }}>
            <Tooltip title="Número de voltas na piscina">
              <Typography 
                color={isCurrentSwimmer ? 'white' : 'inherit'}
                sx={{ 
                  fontSize: { xs: '0.875rem', sm: 'inherit' },
                  textAlign: { xs: 'center', sm: 'left' }
                }}
              >
                Voltas: {totalLaps}
              </Typography>
            </Tooltip>
            <Tooltip title="Distância total percorrida">
              <Typography 
                color={isCurrentSwimmer ? 'white' : 'inherit'}
                sx={{ 
                  fontSize: { xs: '0.875rem', sm: 'inherit' },
                  textAlign: { xs: 'center', sm: 'left' }
                }}
              >
                Dist: {totalDistance}m
              </Typography>
            </Tooltip>
            <Tooltip title="Tempo médio por volta">
              <Typography 
                color={isCurrentSwimmer ? 'white' : 'inherit'}
                sx={{ 
                  fontSize: { xs: '0.875rem', sm: 'inherit' },
                  textAlign: { xs: 'center', sm: 'left' }
                }}
              >
                Média: {formatTime(swimmersData[swimmer]?.average || 0)}
              </Typography>
            </Tooltip>
            <Tooltip title="Tempo da última volta">
              <Typography 
                color={isCurrentSwimmer ? 'white' : 'inherit'}
                sx={{ 
                  fontSize: { xs: '0.875rem', sm: 'inherit' },
                  textAlign: { xs: 'center', sm: 'left' }
                }}
              >
                Últ: {formatTime(getLastValidLapTime())}
              </Typography>
            </Tooltip>
          </Box>
        </Box>
      </Paper>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        onClick={(e) => e.stopPropagation()}
        PaperProps={{
          sx: {
            width: { xs: 'calc(100% - 32px)', sm: 'auto' },
            m: { xs: 2, sm: 4 }
          }
        }}
      >
        <MenuItem 
          onClick={handleEdit}
          disabled={isEditDisabled}
        >
          <EditIcon sx={{ mr: 1 }} />
          Editar
        </MenuItem>
        <MenuItem 
          onClick={handleDelete}
          disabled={isDeleteDisabled}
        >
          <DeleteIcon sx={{ mr: 1 }} />
          Excluir
        </MenuItem>
      </Menu>

      <Dialog
        open={editDialogOpen}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
        sx={{ 
          '& .MuiDialog-paper': { 
            overflow: 'hidden',
            margin: { xs: 1, sm: 2 },
            maxHeight: { xs: '95vh', sm: '80vh' },
            width: { xs: '95%', sm: '80%' }
          }
        }}
      >
        <DialogTitle sx={{ 
          overflow: 'hidden', 
          pb: 1,
          pt: 3,
          px: 3
        }}>
          Editar Nadador
        </DialogTitle>
        <DialogContent sx={{ 
          overflow: 'hidden', 
          px: 3, 
          pt: 4,
          pb: 2 
        }}>
          <TextField
            fullWidth
            label="Nome do Nadador"
            value={editedName}
            onChange={(e) => setEditedName(e.target.value)}
            error={Boolean(nameError)}
            helperText={nameError}
            sx={{ 
              mb: 3,
              '& .MuiInputLabel-root': {
                backgroundColor: '#fff',
                padding: '0 8px'
              },
              '& .MuiInputLabel-shrink': {
                transform: 'translate(14px, -9px) scale(0.75)',
                backgroundColor: '#fff'
              }
            }}
          />
          <Typography variant="subtitle1" gutterBottom>
            Voltas Registradas
          </Typography>
          <List sx={{ 
            maxHeight: { xs: '50vh', sm: '40vh' },
            overflowY: 'auto',
            pr: 1,
            '&::-webkit-scrollbar': {
              width: '6px'
            },
            '&::-webkit-scrollbar-track': {
              background: 'transparent'
            },
            '&::-webkit-scrollbar-thumb': {
              background: '#888',
              borderRadius: '4px'
            }
          }}>
            {editedLaps.map((lap, index) => (
              <React.Fragment key={index}>
                <ListItem sx={{ 
                  py: 0.5,
                  px: 1,
                  overflow: 'hidden',
                  '& .MuiListItemText-root': {
                    margin: 0,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }
                }}>
                  {editingLapIndex === index ? (
                    <Box sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: 1,
                      width: '100%',
                      overflow: 'hidden'
                    }}>
                      <TextField
                        type="number"
                        value={editingLapTime}
                        onChange={(e) => setEditingLapTime(e.target.value)}
                        size="small"
                        fullWidth
                        InputProps={{
                          endAdornment: <InputAdornment position="end">s</InputAdornment>,
                        }}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            '& input': {
                              textAlign: 'center',
                              fontSize: '1.1rem',
                              padding: '8px',
                              '&::-webkit-inner-spin-button': {
                                WebkitAppearance: 'none',
                                opacity: 1,
                                backgroundColor: '#e0e0e0',
                                borderLeft: '1px solid #bdbdbd',
                                borderRadius: '0 4px 4px 0',
                                height: '100%',
                                margin: 0,
                                cursor: 'pointer',
                                display: 'block',
                                width: '48px',
                                position: 'absolute',
                                right: 0,
                                top: 0,
                                backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24'%3E%3Cpath fill='%23424242' d='M7 14l5-5 5 5z'/%3E%3C/svg%3E"), url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24'%3E%3Cpath fill='%23424242' d='M7 10l5 5 5-5z'/%3E%3C/svg%3E")`,
                                backgroundPosition: '50% 25%, 50% 75%',
                                backgroundSize: '16px, 16px',
                                backgroundRepeat: 'no-repeat, no-repeat',
                                '&:active': {
                                  backgroundColor: '#d0d0d0',
                                },
                                '&:hover': {
                                  backgroundColor: '#d5d5d5',
                                }
                              }
                            }
                          },
                          '& input[type=number]': {
                            MozAppearance: 'textfield',
                            '&::-webkit-outer-spin-button': {
                              WebkitAppearance: 'none',
                              margin: 0
                            }
                          }
                        }}
                        inputProps={{
                          step: "0.01",
                          min: "0",
                          style: { 
                            paddingRight: '55px'
                          }
                        }}
                      />
                      <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
                        <IconButton 
                          size="small" 
                          color="primary"
                          onClick={handleSaveLapEdit}
                        >
                          <SaveIcon />
                        </IconButton>
                        <IconButton 
                          size="small" 
                          onClick={handleCancelLapEdit}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                    </Box>
                  ) : (
                    <>
                      <ListItemText 
                        primary={`Volta ${index + 1}: ${formatTime(lap.time)}`}
                        secondary={lap.edited ? 'Editado' : null}
                        sx={{ minWidth: 0 }}
                      />
                      <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
                        <IconButton 
                          size="small"
                          onClick={() => handleStartEditLap(index, lap)}
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton 
                          size="small"
                          onClick={() => handleDeleteLapClick(index)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                    </>
                  )}
                </ListItem>
                {index < editedLaps.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>
        </DialogContent>
        <DialogActions sx={{ overflow: 'hidden', p: 2 }}>
          <Button onClick={handleCloseDialog}>Cancelar</Button>
          <Button 
            onClick={handleSaveEdit}
            variant="contained" 
            color="primary"
            disabled={Boolean(nameError)}
          >
            Salvar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={deleteLapDialogOpen}
        onClose={() => setDeleteLapDialogOpen(false)}
        sx={{ 
          '& .MuiDialog-paper': { 
            overflow: 'hidden',
            margin: { xs: 1, sm: 2 },
            width: { xs: '95%', sm: 'auto' }
          }
        }}
      >
        <DialogTitle sx={{ overflow: 'hidden' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <DeleteIcon color="error" />
            Confirmar Exclusão
          </Box>
        </DialogTitle>
        <DialogContent sx={{ overflow: 'hidden' }}>
          <DialogContentText sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
            Tem certeza que deseja excluir esta volta?
            Esta ação não poderá ser desfeita.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: { xs: 2, sm: 2.5 }, overflow: 'hidden' }}>
          <Button 
            onClick={() => setDeleteLapDialogOpen(false)}
            sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
          >
            Cancelar
          </Button>
          <Button 
            onClick={handleConfirmLapDelete} 
            color="error" 
            variant="contained"
            startIcon={<DeleteIcon />}
            sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
          >
            Excluir
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

SortableSwimmer.propTypes = {
  swimmer: PropTypes.string.isRequired,
  swimmersData: PropTypes.object.isRequired,
  currentSwimmer: PropTypes.string,
  formatTime: PropTypes.func.isRequired,
  isRunning: PropTypes.bool.isRequired,
  isPaused: PropTypes.bool,
  onDelete: PropTypes.func.isRequired,
  onDoubleClick: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired,
  swimmersList: PropTypes.array.isRequired,
  poolLength: PropTypes.number.isRequired,
  raceDistance: PropTypes.number,
  disableEdit: PropTypes.bool
};

export default SortableSwimmer;